// src/components/Account/SecuritySettings.js - Modernized
import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Tab, Tabs, Spinner, Modal, Badge, Form } from 'react-bootstrap';
import { authService } from '../../services/auth';
import TwoFactorSetup from '../Auth/TwoFactorSetup';
import logger from '../../utils/logger';
import { useAuth } from '../../context/AuthContext';
import './ModernAccountStyles.css';

const SecuritySettings = () => {
	const { user, updateUser2FAStatus } = useAuth();
	const [activeTab, setActiveTab] = useState('password');
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
	const [showSetupModal, setShowSetupModal] = useState(false);
	const [showDisableModal, setShowDisableModal] = useState(false);
	const [verificationCode, setVerificationCode] = useState('');
	const [disableLoading, setDisableLoading] = useState(false);

	// Password change fields
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [passwordLoading, setPasswordLoading] = useState(false);

	// Password strength checker
	const [passwordStrength, setPasswordStrength] = useState({
		score: 0,
		feedback: []
	});

	// Fetch user 2FA status
	useEffect(() => {
		const fetch2FAStatus = async () => {
			try {
				setLoading(true);
				setTwoFactorEnabled(user?.twoFactorEnabled || false);
				logger.info(`User 2FA status loaded: ${user?.twoFactorEnabled ? 'enabled' : 'disabled'}`);
			} catch (error) {
				logger.error('Error fetching 2FA status:', error);
				setError('Could not fetch security settings');
			} finally {
				setLoading(false);
			}
		};

		if (user) {
			fetch2FAStatus();
		}
	}, [user]);

	// Password strength evaluation
	useEffect(() => {
		if (newPassword) {
			evaluatePasswordStrength(newPassword);
		} else {
			setPasswordStrength({ score: 0, feedback: [] });
		}
	}, [newPassword]);

	const evaluatePasswordStrength = (password) => {
		let score = 0;
		const feedback = [];

		// Length check
		if (password.length >= 8) score += 1;
		else feedback.push('At least 8 characters');

		if (password.length >= 12) score += 1;
		else if (password.length >= 8) feedback.push('12+ characters recommended');

		// Complexity checks
		if (/[a-z]/.test(password)) score += 1;
		else feedback.push('Include lowercase letters');

		if (/[A-Z]/.test(password)) score += 1;
		else feedback.push('Include uppercase letters');

		if (/[0-9]/.test(password)) score += 1;
		else feedback.push('Include numbers');

		if (/[^A-Za-z0-9]/.test(password)) score += 1;
		else feedback.push('Include special characters');

		setPasswordStrength({ score, feedback });
	};

	const getPasswordStrengthColor = () => {
		if (passwordStrength.score <= 2) return 'danger';
		if (passwordStrength.score <= 4) return 'warning';
		return 'success';
	};

	const getPasswordStrengthText = () => {
		if (passwordStrength.score <= 2) return 'Weak';
		if (passwordStrength.score <= 4) return 'Medium';
		return 'Strong';
	};

	// Handle password change
	const handlePasswordChange = async (e) => {
		e.preventDefault();
		setError('');
		setSuccess('');

		// Validate passwords
		if (!currentPassword) {
			setError('Current password is required');
			return;
		}

		if (!newPassword) {
			setError('New password is required');
			return;
		}

		if (newPassword.length < 8) {
			setError('New password must be at least 8 characters long');
			return;
		}

		if (newPassword !== confirmPassword) {
			setError('New password and confirmation do not match');
			return;
		}

		if (passwordStrength.score < 3) {
			setError('Please choose a stronger password');
			return;
		}

		try {
			setPasswordLoading(true);
			await authService.changePassword(currentPassword, newPassword);

			// Clear form fields
			setCurrentPassword('');
			setNewPassword('');
			setConfirmPassword('');

			setSuccess('Password changed successfully');
		} catch (error) {
			logger.error('Password change error:', error);
			setError(error.message || 'Failed to change password');
		} finally {
			setPasswordLoading(false);
		}
	};

	// Handle enabling 2FA
	const handleEnable2FA = () => {
		setError('');
		setSuccess('');
		setShowSetupModal(true);
	};

	// Handle 2FA setup completion
	const handleSetupComplete = () => {
		setTwoFactorEnabled(true);
		setShowSetupModal(false);
		setSuccess('Two-factor authentication enabled successfully');

		if (updateUser2FAStatus) {
			updateUser2FAStatus(true);
		}
	};

	// Handle 2FA setup cancellation
	const handleSetupCancel = () => {
		setShowSetupModal(false);
	};

	// Handle disabling 2FA
	const handleDisable2FA = async () => {
		try {
			setDisableLoading(true);
			await authService.disable2FA(verificationCode);

			setTwoFactorEnabled(false);
			setShowDisableModal(false);
			setVerificationCode('');
			setSuccess('Two-factor authentication disabled successfully');

			if (updateUser2FAStatus) {
				updateUser2FAStatus(false);
			}
		} catch (error) {
			logger.error('Error disabling 2FA:', error);
			setError(error.message || 'Failed to disable 2FA');
		} finally {
			setDisableLoading(false);
		}
	};

	return (
		<div className="modern-security-settings">
			<div className="modern-page-header">
				<div className="header-glass-bg"></div>
				<div className="header-content">
					<div className="header-icon-wrapper">
						<div className="header-main-icon">
							<i className="bi bi-shield-lock"></i>
						</div>
						<div className="header-text-content">
							<h1 className="page-title">Security Settings</h1>
							<p className="page-subtitle">Manage your account security and authentication</p>
						</div>
					</div>
				</div>
			</div>

			<div className="modern-content-wrapper">
				{/* Modern Alerts */}
				{error && (
					<div className="modern-alert error">
						<div className="alert-icon">
							<i className="bi bi-exclamation-triangle-fill"></i>
						</div>
						<div className="alert-content">
							<strong>Error:</strong> {error}
						</div>
						<button className="alert-close" onClick={() => setError('')}>
							<i className="bi bi-x"></i>
						</button>
					</div>
				)}

				{success && (
					<div className="modern-alert success">
						<div className="alert-icon">
							<i className="bi bi-check-circle-fill"></i>
						</div>
						<div className="alert-content">
							<strong>Success:</strong> {success}
						</div>
						<button className="alert-close" onClick={() => setSuccess('')}>
							<i className="bi bi-x"></i>
						</button>
					</div>
				)}

				{/* Modern Tab Navigation */}
				<div className="modern-tabs-container">
					<div className="modern-tab-nav">
						<button
							className={`modern-tab ${activeTab === 'password' ? 'active' : ''}`}
							onClick={() => setActiveTab('password')}
						>
							<i className="bi bi-key me-2"></i>
							Password
						</button>
						<button
							className={`modern-tab ${activeTab === '2fa' ? 'active' : ''}`}
							onClick={() => setActiveTab('2fa')}
						>
							<i className="bi bi-shield-check me-2"></i>
							Two-Factor Authentication
							{twoFactorEnabled && (
								<span className="tab-badge enabled">
									<i className="bi bi-check-circle"></i>
								</span>
							)}
						</button>
					</div>

					<div className="modern-tab-content">
						{activeTab === 'password' && (
							<div className="modern-card">
								<div className="card-header">
									<div className="card-header-content">
										<div className="card-icon">
											<i className="bi bi-key"></i>
										</div>
										<div>
											<h3>Change Password</h3>
											<p>Update your account password for enhanced security</p>
										</div>
									</div>
								</div>

								<div className="card-body">
									<form onSubmit={handlePasswordChange} className="password-form">
										<div className="form-group">
											<label className="modern-label">Current Password</label>
											<div className="password-input-wrapper">
												<input
													type="password"
													className="modern-input"
													value={currentPassword}
													onChange={(e) => setCurrentPassword(e.target.value)}
													placeholder="Enter your current password"
													required
												/>
											</div>
										</div>

										<div className="form-group">
											<label className="modern-label">New Password</label>
											<div className="password-input-wrapper">
												<input
													type="password"
													className="modern-input"
													value={newPassword}
													onChange={(e) => setNewPassword(e.target.value)}
													placeholder="Enter your new password"
													required
													minLength={8}
												/>
											</div>

											{newPassword && (
												<div className="password-strength">
													<div className="strength-bar">
														<div
															className={`strength-fill ${getPasswordStrengthColor()}`}
															style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
														></div>
													</div>
													<div className="strength-info">
														<span className={`strength-text ${getPasswordStrengthColor()}`}>
															{getPasswordStrengthText()}
														</span>
														{passwordStrength.feedback.length > 0 && (
															<div className="strength-feedback">
																<small>Suggestions:</small>
																<ul>
																	{passwordStrength.feedback.map((item, index) => (
																		<li key={index}>{item}</li>
																	))}
																</ul>
															</div>
														)}
													</div>
												</div>
											)}
										</div>

										<div className="form-group">
											<label className="modern-label">Confirm New Password</label>
											<div className="password-input-wrapper">
												<input
													type="password"
													className="modern-input"
													value={confirmPassword}
													onChange={(e) => setConfirmPassword(e.target.value)}
													placeholder="Confirm your new password"
													required
												/>
											</div>
											{newPassword && confirmPassword && newPassword !== confirmPassword && (
												<div className="password-mismatch">
													<i className="bi bi-exclamation-triangle"></i>
													Passwords do not match
												</div>
											)}
										</div>

										<div className="form-actions">
											<button
												type="submit"
												className="modern-btn modern-btn-primary"
												disabled={passwordLoading || passwordStrength.score < 3}
											>
												{passwordLoading ? (
													<>
														<div className="btn-spinner"></div>
														Changing Password...
													</>
												) : (
													<>
														<i className="bi bi-check-circle"></i>
														Change Password
													</>
												)}
											</button>
										</div>
									</form>
								</div>
							</div>
						)}

						{activeTab === '2fa' && (
							<div className="modern-card">
								<div className="card-header">
									<div className="card-header-content">
										<div className="card-icon">
											<i className="bi bi-shield-check"></i>
										</div>
										<div>
											<h3>Two-Factor Authentication</h3>
											<p>Add an extra layer of security to your account</p>
										</div>
									</div>
								</div>

								<div className="card-body">
									{loading ? (
										<div className="loading-section">
											<div className="modern-spinner small">
												<div className="spinner-ring"></div>
												<div className="spinner-ring"></div>
												<div className="spinner-ring"></div>
											</div>
											<p>Loading 2FA status...</p>
										</div>
									) : (
										<div className="twofa-content">
											<div className="twofa-description">
												<p>
													Two-factor authentication (2FA) significantly enhances your account security.
													When enabled, you'll need both your password and a verification code from your
													authenticator app to sign in.
												</p>
											</div>

											<div className="twofa-status">
												<div className="status-card">
													<div className="status-info">
														<h4>Current Status</h4>
														<div className="status-badge-container">
															{twoFactorEnabled ? (
																<div className="status-badge enabled">
																	<i className="bi bi-shield-check"></i>
																	<span>Enabled</span>
																</div>
															) : (
																<div className="status-badge disabled">
																	<i className="bi bi-shield-x"></i>
																	<span>Disabled</span>
																</div>
															)}
														</div>
													</div>
													<div className="status-actions">
														{twoFactorEnabled ? (
															<button
																className="modern-btn modern-btn-danger"
																onClick={() => setShowDisableModal(true)}
															>
																<i className="bi bi-x-circle"></i>
																Disable 2FA
															</button>
														) : (
															<button
																className="modern-btn modern-btn-primary"
																onClick={handleEnable2FA}
															>
																<i className="bi bi-shield-plus"></i>
																Enable 2FA
															</button>
														)}
													</div>
												</div>
											</div>

											{twoFactorEnabled && (
												<div className="twofa-info">
													<div className="info-banner">
														<div className="banner-icon">
															<i className="bi bi-info-circle"></i>
														</div>
														<div className="banner-content">
															<strong>Backup codes:</strong> If you lose access to your authenticator app,
															you can use your backup codes to sign in. Keep them in a safe place!
														</div>
													</div>
												</div>
											)}

											<div className="security-tips">
												<h4>Security Best Practices</h4>
												<div className="tips-grid">
													<div className="tip-item">
														<div className="tip-icon">
															<i className="bi bi-phone"></i>
														</div>
														<div className="tip-content">
															<h5>Use an Authenticator App</h5>
															<p>Apps like Google Authenticator or Authy are more secure than SMS</p>
														</div>
													</div>
													<div className="tip-item">
														<div className="tip-icon">
															<i className="bi bi-file-text"></i>
														</div>
														<div className="tip-content">
															<h5>Save Backup Codes</h5>
															<p>Store your backup codes in a secure location separate from your device</p>
														</div>
													</div>
													<div className="tip-item">
														<div className="tip-icon">
															<i className="bi bi-arrow-repeat"></i>
														</div>
														<div className="tip-content">
															<h5>Regular Updates</h5>
															<p>Keep your authenticator app updated and verify it works regularly</p>
														</div>
													</div>
												</div>
											</div>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* 2FA Setup Modal */}
			<Modal
				show={showSetupModal}
				onHide={() => setShowSetupModal(false)}
				backdrop="static"
				keyboard={false}
				size="lg"
				centered
				className="modern-modal"
			>
				<Modal.Header closeButton className="modern-modal-header">
					<Modal.Title className="modern-modal-title">
						<div className="modal-icon">
							<i className="bi bi-shield-plus"></i>
						</div>
						Set Up Two-Factor Authentication
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="modern-modal-body">
					<TwoFactorSetup
						onComplete={handleSetupComplete}
						onCancel={handleSetupCancel}
					/>
				</Modal.Body>
			</Modal>

			{/* 2FA Disable Modal */}
			<Modal
				show={showDisableModal}
				onHide={() => setShowDisableModal(false)}
				backdrop="static"
				keyboard={false}
				centered
				className="modern-modal"
			>
				<Modal.Header closeButton className="modern-modal-header">
					<Modal.Title className="modern-modal-title">
						<div className="modal-icon danger">
							<i className="bi bi-shield-x"></i>
						</div>
						Disable Two-Factor Authentication
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="modern-modal-body">
					{error && (
						<div className="modern-alert error">
							<div className="alert-icon">
								<i className="bi bi-exclamation-triangle-fill"></i>
							</div>
							<div className="alert-content">{error}</div>
						</div>
					)}

					<div className="disable-2fa-content">
						<p>
							For security reasons, please enter a verification code from your authenticator app
							to disable two-factor authentication.
						</p>

						<div className="form-group">
							<label className="modern-label">Verification Code</label>
							<input
								type="text"
								className="modern-input"
								value={verificationCode}
								onChange={(e) => setVerificationCode(e.target.value)}
								placeholder="Enter 6-digit code"
								maxLength={6}
								inputMode="numeric"
								pattern="[0-9]*"
							/>
						</div>

						<div className="warning-notice">
							<div className="warning-icon">
								<i className="bi bi-exclamation-triangle"></i>
							</div>
							<div className="warning-content">
								<strong>Warning:</strong> Disabling two-factor authentication will make your account less secure.
							</div>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer className="modern-modal-footer">
					<button
						className="modern-btn modern-btn-secondary"
						onClick={() => {
							setShowDisableModal(false);
							setError('');
						}}
						disabled={disableLoading}
					>
						Cancel
					</button>
					<button
						className="modern-btn modern-btn-danger"
						onClick={handleDisable2FA}
						disabled={disableLoading || !verificationCode}
					>
						{disableLoading ? (
							<>
								<div className="btn-spinner"></div>
								Disabling...
							</>
						) : (
							<>
								<i className="bi bi-shield-x"></i>
								Disable 2FA
							</>
						)}
					</button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default SecuritySettings;